import { Pressable, Text, View, type ViewStyle } from "react-native";
import { Check, X, type LucideIcon } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { getInteractionDelta } from "../utils/interactionStyles";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

/**
 * Selectable pill for filter rows. Audit task 2.5.
 *
 * Canonicalises the GrantsFilterModal chip shape (`rounded-full` pill,
 * deep-brown tinted background when selected, optional check icon). LearnScreen
 * uses a near-identical shape; both should converge on this in the sweep.
 *
 * Variants supported:
 *   - bare label (GrantsFilterModal default)
 *   - label + Check on selected (`showCheck`, default true)
 *   - label + leading icon (`icon`, e.g. AddMedicationModal time-of-day,
 *     LearnScreen topic icons) — icon stays visible in both states,
 *     tinting follows the selected state
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle / selected — bg + border swap, optional check icon
 *   - disabled        — opacity 0.5, presses ignored
 *   - error / focused / loading — N/A; filter chips don't sit inside
 *     FormField chrome and don't carry per-chip validation.
 */
/**
 * Visual context for the chip:
 *   - "default": dark-on-light. Lives inside modals / solid panels where
 *     the page bg is neutral. Inactive uses subtle dark tint, active uses
 *     accent-tinted bg.
 *   - "glass": light-on-glass. Lives on glassy scrolling lists (LearnScreen,
 *     GrantsScreen, FindCareScreen) where the page bg is the warm cream
 *     gradient. Inactive uses white-translucent, active uses a darker
 *     brown tint that reads against the glass.
 */
type Tone = "default" | "glass";

type Props = {
  label: string;
  selected: boolean;
  onPress: () => void;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  /** Hue used for the selected bg/border tint. Defaults to deep brown. */
  accentColor?: string;
  /** Show a check icon on the left when selected. Defaults to true. */
  showCheck?: boolean;
  /** Accessible name override for chips with additional context. */
  accessibilityLabel?: string;
  /** Leading icon (always visible). Tints with selection. */
  icon?: LucideIcon;
  /** Corner radius override. Defaults to 20 (pill). Use 10 for the
   *  square-ish AddMedicationModal time-of-day chip variant. */
  radius?: number;
  /** Visual context. See type doc. Defaults to "default". */
  tone?: Tone;
  disabled?: boolean;
  testID?: string;
  style?: ViewStyle;
  /**
   * When set, the chip renders a trailing X that calls this instead of the
   * check icon. Turns the chip into a removable active-filter token (the chip
   * body still fires onPress). Accessible name for the X override below.
   */
  onRemove?: () => void;
  removeAccessibilityLabel?: string;
};

export function MoonFilterChip({
  label,
  selected,
  onPress,
  action,
  accentColor,
  showCheck = true,
  accessibilityLabel,
  icon: Icon,
  radius = 20,
  tone = "default",
  disabled,
  testID,
  style,
  onRemove,
  removeAccessibilityLabel,
}: Props) {
  const capturePress = usePressCapture("MoonFilterChip");
  const isGlass = tone === "glass";
  const accent = accentColor ?? colors.deepBrown;
  const inactiveBg = isGlass
    ? "rgba(255, 255, 255, 0.4)"
    : "rgba(0, 0, 0, 0.04)";
  const inactiveBorder = isGlass
    ? "rgba(255, 255, 255, 0.5)"
    : "rgba(0, 0, 0, 0.06)";
  const tint = selected ? accent : colors.textMid;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ selected, disabled }}
      // Inert testability hook: react-native-web 0.21 no longer forwards
      // accessibilityState.selected to the DOM, so mirror the selected state
      // via the aria-selected prop (RN forwards it, RNW renders it as the
      // aria-selected attribute) for deterministic E2E assertions. Purely
      // observational — does not affect behavior.
      aria-selected={selected}
      onPress={() => {
        capturePress(pressLabel(label, accessibilityLabel), action);
        onPress();
      }}
      disabled={disabled}
      testID={testID}
    >
      {({ hovered, focused }) => (
        <View
          style={[
            {
              flexDirection: "row",
              alignItems: "center",
              paddingHorizontal: 14,
              paddingVertical: isGlass ? 7 : 9,
              borderRadius: radius,
              borderWidth: 1,
              backgroundColor: selected ? `${accent}18` : inactiveBg,
              borderColor: selected ? `${accent}30` : inactiveBorder,
              opacity: disabled ? 0.7 : 1,
            },
            style,
            getInteractionDelta({ hovered, focused }),
          ]}
        >
          {Icon ? (
            <Icon size={14} color={tint} style={{ marginRight: 6 }} />
          ) : null}
          {selected && showCheck && !onRemove ? (
            <View
              style={{
                marginRight: 6,
                marginLeft: Icon ? 0 : -2,
              }}
            >
              <Check size={13} color={accent} strokeWidth={2.5} />
            </View>
          ) : null}
          <Text
            style={{
              fontFamily: selected
                ? typography.families.sansMedium
                : typography.families.sans,
              fontSize: 13,
              fontWeight: selected ? "600" : "400",
              color: tint,
            }}
          >
            {label}
          </Text>
          {onRemove ? (
            <Pressable
              onPress={() => {
                capturePress("filter_chip_remove", "filter_chip_remove");
                onRemove();
              }}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={removeAccessibilityLabel}
              style={{ marginLeft: 6, marginRight: -2 }}
            >
              <X size={13} color={tint} strokeWidth={2} />
            </Pressable>
          ) : null}
        </View>
      )}
    </Pressable>
  );
}
